import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import moment from 'moment';
import usePayments from '../../pages/Contract/ContractWizard/components/PaymentsManager/hooks/usePayments';
import { useCurrencyFormatter } from '../Hooks/useCurrencyFormatter';
import { useVatRates } from '../Hooks/useVatRates';
import AddUnitPicker from './AddUnitPicker';
import { palette, unitLabel, unitIcon } from './contractPaymentsShared';
import { PAYMENT_TYPE_OPTIONS } from '../../common/paymentTypes';

const inputStyle = {
  border: `1px solid ${palette.outlineVariant}55`,
  borderRadius: '8px',
  padding: '8px 10px',
  fontWeight: 600,
  color: palette.onSurface,
  background: palette.surfaceContainerLowest,
  width: '100%',
};

const cardStyle = {
  background: palette.surfaceContainerLowest,
  borderRadius: '16px',
  padding: '24px',
  border: `1px solid ${palette.outlineVariant}22`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  height: '100%',
};

const r2 = (x) => Math.round(x * 100) / 100;
const num = (v) => parseFloat(v) || 0;

// Dated payments in date order; undated ones after them, keeping their
// current relative order (so they can still be arranged by dragging).
const compareByDate = (a, b) => {
  if (a.date && b.date) return a.date < b.date ? -1 : a.date > b.date ? 1 : a.i - b.i;
  if (a.date) return -1;
  if (b.date) return 1;
  return a.i - b.i;
};
const sortByDate = (list) =>
  list
    .map((p, i) => ({ p, i, date: p.booking_date }))
    .sort(compareByDate)
    .map((x) => x.p);

const typeOptionsFor = (currentType) =>
  PAYMENT_TYPE_OPTIONS.filter(
    (opt) => (opt.value !== 'BALANCE' && opt.value !== 'EXTRA') || opt.value === currentType
  );

const EditPaymentModal = ({ isOpen, toggle, editForm, onSave }) => {
  const [payments, setPayments] = useState([]);
  const [unitPrices, setUnitPrices] = useState([]); // [{ unitId, label, icon, settled_price (amount, excl. extra), extra }]
  const [extrasRow, setExtrasRow] = useState({ id: null, booking_date: null, vat: null, isPaid: false });
  const [balanceRow, setBalanceRow] = useState({ id: null, booking_date: null, vat: null, isPaid: false });
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const initialSnapshotRef = useRef(null);
  const { formatCurrency } = useCurrencyFormatter();
  const ops = usePayments({ payments, onChange: setPayments });
  const { vatRates, loading: vatRatesLoading } = useVatRates();

  const hasChanges = initialSnapshotRef.current
    ? JSON.stringify({ payments, unitPrices, extrasRow, balanceRow }) !== JSON.stringify(initialSnapshotRef.current)
    : false;

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      ops.reorderPayments(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const contract = editForm?.contract || null;
  const currency = editForm?.contract?.currency || 'EUR';
  // Payments and settlement prices are always stored/edited in EUR regardless
  // of the contract's currency — RON is shown as a secondary, converted
  // figure below, never as the editable value's own label.
  const currencySymbol = '€';
  const exchangeRate = editForm?.contract?.exchange_rate ?? null;
  const showRon = currency === 'RON' && exchangeRate != null;

  // Totals. A unit's price is amount + extra; the contract total is the sum.
  const baseTotal = r2(unitPrices.reduce((sum, u) => sum + num(u.settled_price), 0));
  const extrasTotal = r2(unitPrices.reduce((sum, u) => sum + num(u.extra), 0));
  const settledPrice = r2(baseTotal + extrasTotal); // contract total
  const lockedExtras = r2(payments.filter((p) => p.locked && p.type === 'EXTRA').reduce((s, p) => s + num(p.amount), 0));
  const extrasAmount = r2(Math.max(extrasTotal - lockedExtras, 0));
  const showExtrasRow = extrasAmount > 0;
  const manualTotal = r2(ops.getTotalPayments());
  // The balance is always the last payment and takes whatever is left.
  const balance = r2(settledPrice - manualTotal - extrasAmount);
  const totalPayments = r2(manualTotal + extrasAmount); // scheduled before the balance
  const percentSettled = settledPrice > 0 ? Math.min(100, (totalPayments / settledPrice) * 100) : 0;

  // Display position of every row: the payments and the generated EXTRA row
  // sorted together by date (the EXTRA row is a real payment with its own
  // due date), then the BALANCE, which is always last. Rendered through the
  // CSS `order` property so the rows' own markup stays separate.
  const rowRank = (() => {
    const items = payments.map((p, i) => ({ key: i, i, date: p.booking_date }));
    if (showExtrasRow) items.push({ key: 'extras', i: payments.length, date: extrasRow.booking_date });
    const rank = {};
    items.sort(compareByDate).forEach((item, pos) => {
      rank[item.key] = pos + 1;
    });
    rank.balance = items.length + 1;
    return rank;
  })();
  const currentVAT =
    payments.length > 0 && payments[0].vat != null
      ? String(payments[0].vat)
      : balanceRow.vat != null
      ? String(balanceRow.vat)
      : '';

  useEffect(() => {
    if (isOpen && editForm) {
      setSaveError(null);
      setShowValidation(false);
      const validPayments = Array.isArray(editForm.payments) ? editForm.payments : [];

      const formattedPayments = validPayments.map((payment) => ({
        id: payment.id,
        type: payment.type || '',
        amount: String(payment.amount || 0),
        amount_ron: payment.amount_ron ?? null,
        booking_date: payment.booking_date ? moment(payment.booking_date).format('YYYY-MM-DD') : null,
        isPaid: !!payment.cashed,
        cashed: payment.cashed,
        cashed_date: payment.cashed_date,
        vat: payment.vat ?? null,
        invoice: payment.invoice || null,
        // Locked as of load time: an invoiced payment is a settled historical
        // fact and must stay read-only. A payment merely marked paid (no
        // invoice) can still be toggled back/edited freely.
        locked: !!payment.invoice,
      }));

      const units = Array.isArray(contract?.units) ? contract.units : [];
      const formattedUnits = units.map((unit) => {
        // settled_price is the amount without extras; extra_finishing is stored separately.
        const extra = num(unit.settlement_price?.extra_finishing);
        return {
          unitId: unit.id,
          label: unitLabel(unit) || `Unit ${unit.id}`,
          icon: unitIcon(unit),
          settled_price: String(unit.settlement_price?.settled_price ?? 0),
          extra: extra ? String(extra) : '',
        };
      });
      const unitExtras = formattedUnits.reduce((s, u) => s + num(u.extra), 0);

      const generated = (p) => !p.locked && (p.type === 'BALANCE' || (p.type === 'EXTRA' && unitExtras > 0));
      const firstOf = (type) => formattedPayments.find((p) => generated(p) && p.type === type);
      const manual = sortByDate(formattedPayments.filter((p) => !generated(p)));
      const defaultVat = manual[0]?.vat ?? formattedPayments[0]?.vat ?? null;
      const toRow = (p) => ({
        id: p?.id ?? null,
        booking_date: p?.booking_date ?? null,
        vat: p?.vat ?? defaultVat,
        isPaid: !!p?.isPaid,
      });
      const nextExtras = toRow(firstOf('EXTRA'));
      const nextBalance = toRow(firstOf('BALANCE'));

      setPayments(manual);
      setUnitPrices(formattedUnits);
      setExtrasRow(nextExtras);
      setBalanceRow(nextBalance);
      initialSnapshotRef.current = { payments: manual, unitPrices: formattedUnits, extrasRow: nextExtras, balanceRow: nextBalance };
    }

  }, [isOpen, editForm]);

  const handleUnitPriceChange = (unitId, value) => {
    setUnitPrices((prev) =>
      prev.map((unit) => (unit.unitId === unitId ? { ...unit, settled_price: value } : unit))
    );
  };

  const handleUnitExtraChange = (unitId, value) => {
    setUnitPrices((prev) => prev.map((unit) => (unit.unitId === unitId ? { ...unit, extra: value } : unit)));
  };

  // Amounts keep cents (the shared wizard hook strips them): the balance is
  // computed to the cent, and the backend checks the total exactly.
  // Changing a date re-sorts the payments by date straight away.
  const handleDateChange = (index, value) => {
    setPayments((prev) => sortByDate(prev.map((p, i) => (i === index ? { ...p, booking_date: value || null } : p))));
  };

  const handleAmountChange = (index, raw) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*?)\./g, '$1');
    setPayments((prev) => prev.map((p, i) => (i === index ? { ...p, amount: cleaned } : p)));
  };

  const handleStatusChange = (index, isPaid) => {
    setPayments((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, isPaid, cashed: isPaid ? num(p.amount) || null : null, cashed_date: isPaid ? p.booking_date : null } : p
      )
    );
  };

  const addPayment = () => {
    setPayments((prev) => [
      ...prev,
      {
        type: prev.some((p) => !p.locked) ? 'PAYMENT' : 'DOWN_PAYMENT',
        vat: prev[0]?.vat ?? balanceRow.vat ?? null,
        booking_date: null,
        isPaid: false,
        cashed_date: null,
        amount: '',
      },
    ]);
  };

  const handleVATChangeAll = (value) => {
    ops.handleVATChange(value);
    const vat = value === '' ? null : parseInt(value, 10);
    setExtrasRow((row) => ({ ...row, vat }));
    setBalanceRow((row) => ({ ...row, vat }));
  };

  const handleAddUnit = (unit) => {
    setUnitPrices((prev) => [
      ...prev,
      {
        unitId: unit.id,
        label: unitLabel({ type: unit.unit_type, nr: unit.nr, block: unit.block?.name }) || `Unit ${unit.id}`,
        icon: unitIcon({ type: unit.unit_type }),
        settled_price: String(unit.initial_price ?? 0),
        extra: '',
      },
    ]);
  };

  const handleRemoveUnit = (unitId) => {
    setUnitPrices((prev) => prev.filter((unit) => unit.unitId !== unitId));
  };

  // A balance payment settles whatever is left at closing, so it doesn't need a
  // scheduled date the way every other payment type does. Mirrors the backend rule.
  const isBookingDateMissing = (payment) =>
    !payment.locked && payment.type !== 'BALANCE' && !payment.booking_date;

  const handleSave = async () => {
    if (!hasChanges) return;

    if (unitPrices.length === 0) {
      setSaveError('Add at least one unit before saving.');
      return;
    }

    if (unitPrices.some((u) => num(u.settled_price) <= 0)) {
      setSaveError('Every unit needs an amount.');
      return;
    }

    if (payments.some((p) => !p.locked && !p.type)) {
      setShowValidation(true);
      setSaveError('Pick a type for every payment.');
      return;
    }

    if (payments.some((p) => !p.locked && num(p.amount) <= 0)) {
      setShowValidation(true);
      setSaveError('Every payment needs an amount.');
      return;
    }

    if (payments.some(isBookingDateMissing) || (showExtrasRow && !extrasRow.booking_date)) {
      setShowValidation(true);
      setSaveError('Booking date is required for all payment types except Balance.');
      return;
    }

    if (balance < -0.005) {
      setSaveError(`Payments exceed the contract total by ${formatCurrency(-balance, 'EUR')} — reduce a payment.`);
      return;
    }
    
    const finalPayments = [...payments];
    if (showExtrasRow) {
      finalPayments.push({
        id: extrasRow.id,
        type: 'EXTRA',
        amount: String(extrasAmount),
        booking_date: extrasRow.booking_date,
        vat: extrasRow.vat,
        isPaid: extrasRow.isPaid,
      });
    }
    if (balance > 0.005) {
      finalPayments.push({
        id: balanceRow.id,
        type: 'BALANCE',
        amount: String(balance),
        booking_date: balanceRow.booking_date || null,
        vat: balanceRow.vat,
        isPaid: balanceRow.isPaid,
      });
    }

    setSaveError(null);
    setShowValidation(false);
    setSaving(true);
    const result = await onSave(finalPayments, unitPrices);
    setSaving(false);

    // On success the parent (onSave) already closes the modal itself; calling
    // toggle() here too would flip it back open right after it closes.
    if (result && !result.success) {
      setSaveError(result.message || 'Failed to save changes.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" contentClassName="border-0 bg-transparent">
      <div
        style={{
          background: palette.surfaceContainerLowest,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(25, 28, 30, 0.16)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 32px',
            borderBottom: `1px solid ${palette.outlineVariant}33`,
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontWeight: 800, color: palette.onSurface }}>Manage Contract Payments</h4>
            <p style={{ margin: '4px 0 0', color: palette.onSurfaceVariant, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ri-file-list-3-line" />
              Contract ID: <span style={{ fontWeight: 700, color: palette.primary }}>{contract?.pvc_number}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={toggle}
            aria-label="Close"
            style={{
              border: 'none',
              background: 'transparent',
              color: palette.onSurfaceVariant,
              fontSize: '22px',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {saveError && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              background: '#fdecea',
              border: '1px solid #f5c2c0',
              borderLeft: 'none',
              borderRight: 'none',
              color: '#7a1f1a',
              padding: '14px 32px',
              fontWeight: 600,
              fontSize: '0.9rem',
              flexShrink: 0,
            }}
          >
            <i className="ri-error-warning-line" style={{ fontSize: 18, marginTop: 1 }} />
            <span>{saveError}</span>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', background: palette.surface }}>
          <div className="row g-4">
            {/* Settlement Prices */}
            <div className="col-12 col-md-8">
                <div style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <h6 style={{ margin: 0, fontWeight: 800, color: palette.primary, flex: 1 }}>Settlement Prices</h6>
                    <span
                      style={{
                        background: palette.surfaceContainer,
                        color: palette.onSurfaceVariant,
                        borderRadius: '999px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {unitPrices.length} unit{unitPrices.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAddUnitOpen(true)}
                      style={{
                        font: 'inherit',
                        fontSize: 12.5,
                        fontWeight: 700,
                        padding: '7px 12px',
                        borderRadius: 8,
                        border: `1px solid ${palette.outlineVariant}88`,
                        background: palette.surfaceContainerLowest,
                        color: palette.primary,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      + Add unit
                    </button>
                  </div>

                  {unitPrices.length === 0 && (
                    <div
                      style={{
                        border: `1px dashed ${palette.outlineVariant}88`,
                        borderRadius: 11,
                        padding: '26px 18px',
                        textAlign: 'center',
                        fontSize: 13,
                        color: palette.onSurfaceVariant,
                      }}
                    >
                      No units on this contract. Add at least one to set a settlement price.
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {unitPrices.map((unit) => (
                      <div
                        key={unit.unitId}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '14px 16px',
                          background: palette.surface,
                          borderRadius: '12px',
                          border: `1px solid ${palette.outlineVariant}15`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: palette.surfaceContainer,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: palette.onSurfaceVariant,
                              flexShrink: 0,
                            }}
                          >
                            <i className={unit.icon} style={{ fontSize: 18 }} />
                          </div>
                          <span
                            style={{
                              fontWeight: 600,
                              color: palette.onSurface,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {unit.label}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: palette.surfaceContainerLowest,
                                border: `1px solid ${palette.outlineVariant}33`,
                                borderRadius: '8px',
                                padding: '8px 12px',
                                width: 170,
                                height: 36,
                              }}
                            >
                              <span style={{ color: palette.onSurfaceVariant, marginRight: 8 }}>{currencySymbol}</span>
                              <input
                                type="number"
                                min="0"
                                value={unit.settled_price}
                                onChange={(e) => handleUnitPriceChange(unit.unitId, e.target.value)}
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent',
                                  width: '100%',
                                  textAlign: 'right',
                                  fontWeight: 600,
                                  color: palette.onSurface,
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 11, color: palette.onSurfaceVariant }}>
                              Amount{showRon ? ` · ${formatCurrency(num(unit.settled_price) * exchangeRate, 'RON')}` : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: palette.surfaceContainerLowest,
                                border: `1px solid ${palette.outlineVariant}33`,
                                borderRadius: '8px',
                                padding: '8px 12px',
                                width: 130,
                                height: 36,
                              }}
                            >
                              <span style={{ color: palette.onSurfaceVariant, marginRight: 8 }}>{currencySymbol}</span>
                              <input
                                type="number"
                                min="0"
                                value={unit.extra}
                                placeholder="0"
                                onChange={(e) => handleUnitExtraChange(unit.unitId, e.target.value)}
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent',
                                  width: '100%',
                                  textAlign: 'right',
                                  fontWeight: 600,
                                  color: palette.onSurface,
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 11, color: palette.onSurfaceVariant }}>
                              Extra{showRon && num(unit.extra) > 0 ? ` · ${formatCurrency(num(unit.extra) * exchangeRate, 'RON')}` : ''}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveUnit(unit.unitId)}
                            title="Remove unit from contract"
                            style={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 36,
                              height: 36,
                              border: `1px solid ${palette.outlineVariant}55`,
                              borderRadius: 8,
                              background: palette.surfaceContainerLowest,
                              color: '#b8332f',
                              cursor: 'pointer',
                            }}
                          >
                            <i className="ri-delete-bin-line" style={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      marginTop: 20,
                      paddingTop: 16,
                      borderTop: `1px solid ${palette.outlineVariant}33`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        color: palette.onSurfaceVariant,
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Total Settlement Price
                    </span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 800, fontSize: '1.4rem', color: palette.primary }}>
                        {formatCurrency(settledPrice, 'EUR')}
                      </span>
                      {extrasTotal > 0 && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: palette.onSurfaceVariant }}>
                          {formatCurrency(baseTotal, 'EUR')} + {formatCurrency(extrasTotal, 'EUR')} extras
                        </div>
                      )}
                      {showRon && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: palette.onSurfaceVariant }}>
                          {formatCurrency(settledPrice * exchangeRate, 'RON')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
            </div>

            {/* Settlement Status gauge */}
            <div className={unitPrices.length > 0 ? 'col-12 col-md-4' : 'col-12 col-md-6'}>
              <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <h6 style={{ margin: '0 0 20px', fontWeight: 800, color: palette.primary, alignSelf: 'flex-start' }}>
                  Settlement Status
                </h6>
                <div style={{ position: 'relative', width: 130, height: 130, marginBottom: 12 }}>
                  <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={palette.surfaceContainer}
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={palette.primary}
                      strokeWidth="3"
                      strokeDasharray={`${percentSettled}, 100`}
                    />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: '1.5rem', color: palette.primary }}>
                      {Math.round(percentSettled)}%
                    </span>
                  </div>
                </div>
                <span style={{ fontWeight: 700, color: palette.primary, fontSize: '0.85rem' }}>
                  {formatCurrency(totalPayments, 'EUR')} Settled
                </span>
                <span style={{ color: palette.onSurfaceVariant, fontSize: '0.75rem' }}>
                  of {formatCurrency(settledPrice, 'EUR')}
                </span>
                {showRon && (
                  <span style={{ color: palette.onSurfaceVariant, fontSize: '0.7rem', marginTop: 2 }}>
                    {formatCurrency(totalPayments * exchangeRate, 'RON')} of {formatCurrency(settledPrice * exchangeRate, 'RON')}
                  </span>
                )}
              </div>
            </div>

            {/* Metrics row */}
            <div className="col-12 col-md-4">
              <div
                style={{
                  background: `${palette.primaryFixed}80`,
                  border: `1px solid ${palette.primaryFixedDim}88`,
                  borderRadius: '16px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: palette.onPrimaryFixedVariant, textTransform: 'uppercase' }}>
                    Total Value
                  </span>
                  <i className="ri-wallet-3-line" style={{ color: palette.onPrimaryFixedVariant, fontSize: 20 }} />
                </div>
                <span style={{ fontWeight: 800, fontSize: '1.3rem', color: palette.primary }}>
                  {formatCurrency(settledPrice, 'EUR')}
                </span>
                {showRon && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: palette.onPrimaryFixedVariant }}>
                    {formatCurrency(settledPrice * exchangeRate, 'RON')}
                  </span>
                )}
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div style={{ ...cardStyle, padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: palette.onSurfaceVariant, textTransform: 'uppercase' }}>
                    Balance (remainder)
                  </span>
                  <i className="ri-checkbox-circle-line" style={{ color: palette.outlineVariant, fontSize: 20 }} />
                </div>
                <span style={{ fontWeight: 800, fontSize: '1.3rem', color: balance < -0.005 ? '#b3312f' : palette.onSurface }}>
                  {formatCurrency(balance, 'EUR')}
                </span>
                {showRon && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: palette.onSurfaceVariant }}>
                    {formatCurrency(balance * exchangeRate, 'RON')}
                  </span>
                )}
                <span style={{ fontSize: 11.5, color: balance < -0.005 ? '#b3312f' : palette.onSurfaceVariant, marginTop: 2 }}>
                  {balance < -0.005 ? 'Over the total — reduce a payment' : balance <= 0.005 ? 'Nothing left for the balance' : 'Added automatically as the last payment'}
                </span>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div style={{ ...cardStyle, padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: palette.onSurfaceVariant, textTransform: 'uppercase' }}>
                    VAT Rate
                  </span>
                  <i className="ri-receipt-line" style={{ color: palette.outlineVariant, fontSize: 20 }} />
                </div>
                <select
                  value={currentVAT}
                  onChange={(e) => handleVATChangeAll(e.target.value)}
                  disabled={vatRatesLoading}
                  style={{
                    background: palette.surfaceContainer,
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    fontWeight: 700,
                    color: palette.onSurface,
                  }}
                >
                  <option value="">{vatRatesLoading ? 'Loading…' : 'Select VAT'}</option>
                  {vatRates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.percentage}%{rate.name ? ` · ${rate.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Payments Ledger */}
            <div className="col-12">
              <div
                style={{
                  background: palette.surfaceContainerLowest,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${palette.outlineVariant}22`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px',
                    borderBottom: `1px solid ${palette.outlineVariant}22`,
                    background: palette.surface,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h6 style={{ margin: 0, fontWeight: 800, color: palette.primary }}>Payments Ledger</h6>
                    <span
                      style={{
                        background: `${palette.primary}1a`,
                        color: palette.primary,
                        borderRadius: '999px',
                        padding: '2px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {payments.length + (showExtrasRow ? 1 : 0) + (balance > 0.005 ? 1 : 0)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={addPayment}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryContainer})`,
                      color: palette.onPrimary,
                      border: 'none',
                      borderRadius: '10px',
                      padding: '10px 18px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <i className="ri-add-line" />
                    Add Payment
                  </button>
                </div>

                <div style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      order: 0,
                      minWidth: 880,
                      display: 'grid',
                      gridTemplateColumns: '64px 2fr 1.4fr 2fr 1.6fr 1.4fr 60px',
                      gap: 12,
                      padding: '12px 16px',
                      background: palette.surface,
                      borderBottom: `1px solid ${palette.outlineVariant}22`,
                      fontSize: 11,
                      fontWeight: 700,
                      color: palette.onSurfaceVariant,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>#</div>
                    <div>Payment Type</div>
                    <div>Date</div>
                    <div>Amount &amp; Weight</div>
                    <div>Status</div>
                    <div>VAT</div>
                    <div style={{ textAlign: 'right' }}>Actions</div>
                  </div>

                  {payments.map((payment, index) => {
                    const amount = parseFloat(payment.amount) || 0;
                    const weight = settledPrice > 0 ? (amount / settledPrice) * 100 : 0;
                    const locked = !!payment.locked;
                    const lockedTitle = 'Cannot edit: this payment already has an invoice';
                    const dateMissing = showValidation && isBookingDateMissing(payment);
                    const isDragging = draggedIndex === index;
                    const isDragOver = dragOverIndex === index && draggedIndex !== index;
                    return (
                      <div
                        key={payment.id ?? `new-${index}`}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{
                          order: rowRank[index],
                          minWidth: 880,
                          display: 'grid',
                          gridTemplateColumns: '64px 2fr 1.4fr 2fr 1.6fr 1.4fr 60px',
                          gap: 12,
                          padding: '14px 16px',
                          alignItems: 'center',
                          borderBottom: `1px solid ${palette.outlineVariant}18`,
                          background: isDragOver
                            ? `${palette.primary}0f`
                            : (locked ? palette.surface : 'transparent'),
                          outline: isDragOver ? `2px dashed ${palette.primary}66` : 'none',
                          outlineOffset: -2,
                          opacity: isDragging ? 0.4 : 1,
                          transition: 'background 0.15s, opacity 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <i
                            className="ri-draggable"
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            title="Drag to reorder"
                            style={{ cursor: 'grab', color: palette.onSurfaceVariant, fontSize: 15 }}
                          />
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              background: palette.primaryFixed,
                              color: palette.primary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {locked ? <i className="ri-lock-line" style={{ fontSize: 12 }} /> : rowRank[index]}
                          </span>
                        </div>

                        <select
                          value={payment.type}
                          onChange={(e) => ops.handleTypeChange(index, e.target.value)}
                          disabled={locked}
                          title={locked ? lockedTitle : undefined}
                          style={{ ...inputStyle, ...(locked ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                        >
                          <option value="">Select type</option>
                          {typeOptionsFor(payment.type).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>

                        <div>
                          <input
                            type="date"
                            value={payment.booking_date || ''}
                            onChange={(e) => handleDateChange(index, e.target.value)}
                            disabled={locked}
                            title={
                              locked
                                ? lockedTitle
                                : dateMissing
                                ? 'Booking date is required unless payment type is Balance'
                                : undefined
                            }
                            style={{
                              ...inputStyle,
                              ...(locked ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                              ...(dateMissing ? { border: '1px solid #d64545', background: '#fff5f5' } : {}),
                            }}
                          />
                          {dateMissing && (
                            <div style={{ fontSize: 10, color: '#d64545', marginTop: 4, fontWeight: 600 }}>
                              Required unless Balance
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                border: `1px solid ${palette.outlineVariant}55`,
                                borderRadius: '8px',
                                padding: '8px 10px',
                                opacity: locked ? 0.6 : 1,
                              }}
                            >
                              <span style={{ color: palette.onSurfaceVariant, marginRight: 6, fontSize: 12 }}>{currencySymbol}</span>
                              <input
                                type="text"
                                value={payment.amount}
                                onChange={(e) => handleAmountChange(index, e.target.value)}
                                disabled={locked}
                                title={locked ? lockedTitle : undefined}
                                style={{ border: 'none', outline: 'none', width: '100%', textAlign: 'right', fontWeight: 600, background: 'transparent', cursor: locked ? 'not-allowed' : 'text' }}
                              />
                            </div>
                            {showRon && (
                              <div style={{ textAlign: 'right', fontSize: 11, color: palette.onSurfaceVariant, marginTop: 3 }}>
                                {formatCurrency(
                                  locked && payment.amount_ron != null ? payment.amount_ron : amount * exchangeRate,
                                  'RON'
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ width: 56, flexShrink: 0 }}>
                            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 700, color: palette.onSurfaceVariant, marginBottom: 4 }}>
                              {weight.toFixed(1)}%
                            </div>
                            <div style={{ height: 6, borderRadius: 999, background: palette.surfaceContainer, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.min(weight, 100)}%`, background: palette.primary, borderRadius: 999 }} />
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            role="switch"
                            aria-checked={payment.isPaid}
                            aria-disabled={locked}
                            tabIndex={locked ? -1 : 0}
                            title={locked ? lockedTitle : undefined}
                            onClick={() => !locked && handleStatusChange(index, !payment.isPaid)}
                            onKeyDown={(e) => !locked && e.key === 'Enter' && handleStatusChange(index, !payment.isPaid)}
                            style={{
                              position: 'relative',
                              width: 32,
                              height: 18,
                              borderRadius: 999,
                              background: payment.isPaid ? palette.primary : palette.surfaceDim,
                              display: 'inline-block',
                              cursor: locked ? 'not-allowed' : 'pointer',
                              opacity: locked ? 0.6 : 1,
                              transition: 'background 0.2s',
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                top: 2,
                                left: payment.isPaid ? 16 : 2,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                background: palette.surfaceContainerLowest,
                                transition: 'left 0.2s',
                              }}
                            />
                          </span>
                          <span
                            style={{
                              background: payment.isPaid ? `${palette.primary}1a` : palette.tertiaryFixed,
                              color: payment.isPaid ? palette.primary : palette.onTertiaryFixedVariant,
                              borderRadius: 6,
                              padding: '2px 8px',
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {payment.isPaid ? 'Settled' : 'Pending'}
                          </span>
                        </div>

                        <select
                          value={payment.vat != null ? String(payment.vat) : ''}
                          onChange={(e) => ops.handleVATChangeForIndex(index, e.target.value)}
                          disabled={locked || vatRatesLoading}
                          title={locked ? lockedTitle : undefined}
                          style={{ ...inputStyle, ...(locked ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                        >
                          <option value="">{vatRatesLoading ? 'Loading…' : 'Select VAT'}</option>
                          {vatRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.percentage}%{rate.name ? ` · ${rate.name}` : ''}
                            </option>
                          ))}
                        </select>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => !locked && ops.deletePayment(index)}
                            disabled={locked}
                            title={locked ? lockedTitle : 'Delete'}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: palette.onSurfaceVariant,
                              padding: 6,
                              borderRadius: 8,
                              cursor: locked ? 'not-allowed' : 'pointer',
                              opacity: locked ? 0.5 : 1,
                            }}
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {[
                    showExtrasRow && {
                      key: 'extras',
                      row: extrasRow,
                      setRow: setExtrasRow,
                      title: 'Extra',
                      note: 'Sum of unit extras · generated',
                      amount: extrasAmount,
                      dateRequired: true,
                    },
                    {
                      key: 'balance',
                      row: balanceRow,
                      setRow: setBalanceRow,
                      title: 'Balance',
                      note: 'Always last · takes the remainder',
                      amount: balance,
                      dateRequired: false,
                    },
                  ]
                    .filter(Boolean)
                    .map(({ key, row, setRow, title, note, amount, dateRequired }) => {
                      const dateMissing = showValidation && dateRequired && !row.booking_date;
                      const negative = amount < -0.005;
                      const weight = settledPrice > 0 ? (Math.max(amount, 0) / settledPrice) * 100 : 0;
                      return (
                        <div
                          key={key}
                          style={{
                            order: rowRank[key],
                            minWidth: 880,
                            display: 'grid',
                            gridTemplateColumns: '64px 2fr 1.4fr 2fr 1.6fr 1.4fr 60px',
                            gap: 12,
                            padding: '14px 16px',
                            alignItems: 'center',
                            borderBottom: `1px solid ${palette.outlineVariant}18`,
                            background: key === 'balance' ? `${palette.primary}08` : palette.surface,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                background: palette.primaryFixed,
                                color: palette.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {rowRank[key]}
                            </span>
                          </div>

                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: palette.onSurface }}>{title}</div>
                            <div style={{ fontSize: 11, color: palette.onSurfaceVariant, marginTop: 2 }}>{note}</div>
                          </div>

                          <div>
                            <input
                              type="date"
                              value={row.booking_date || ''}
                              onChange={(e) => setRow((r) => ({ ...r, booking_date: e.target.value || null }))}
                              title={dateRequired ? undefined : 'Optional — the balance is usually paid at handover'}
                              style={{
                                ...inputStyle,
                                ...(dateMissing ? { border: '1px solid #d64545', background: '#fff5f5' } : {}),
                              }}
                            />
                            {!dateRequired && !row.booking_date && (
                              <div style={{ fontSize: 10, color: palette.onSurfaceVariant, marginTop: 4, fontWeight: 600 }}>Optional · paid at handover</div>
                            )}
                            {dateMissing && (
                              <div style={{ fontSize: 10, color: '#d64545', marginTop: 4, fontWeight: 600 }}>Required</div>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  padding: '8px 10px',
                                  fontWeight: 700,
                                  color: negative ? '#b3312f' : palette.onSurface,
                                }}
                                title="Calculated automatically"
                              >
                                {formatCurrency(amount, 'EUR')}
                              </div>
                              {showRon && (
                                <div style={{ textAlign: 'right', fontSize: 11, color: palette.onSurfaceVariant, marginTop: 3 }}>
                                  {formatCurrency(amount * exchangeRate, 'RON')}
                                </div>
                              )}
                            </div>
                            <div style={{ width: 56, flexShrink: 0 }}>
                              <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 700, color: palette.onSurfaceVariant, marginBottom: 4 }}>
                                {weight.toFixed(1)}%
                              </div>
                              <div style={{ height: 6, borderRadius: 999, background: palette.surfaceContainer, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.min(weight, 100)}%`, background: palette.primary, borderRadius: 999 }} />
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span
                              role="switch"
                              aria-checked={row.isPaid}
                              tabIndex={0}
                              onClick={() => setRow((r) => ({ ...r, isPaid: !r.isPaid }))}
                              onKeyDown={(e) => e.key === 'Enter' && setRow((r) => ({ ...r, isPaid: !r.isPaid }))}
                              style={{
                                position: 'relative',
                                width: 32,
                                height: 18,
                                borderRadius: 999,
                                background: row.isPaid ? palette.primary : palette.surfaceDim,
                                display: 'inline-block',
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  position: 'absolute',
                                  top: 2,
                                  left: row.isPaid ? 16 : 2,
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  background: palette.surfaceContainerLowest,
                                  transition: 'left 0.2s',
                                }}
                              />
                            </span>
                            <span
                              style={{
                                background: row.isPaid ? `${palette.primary}1a` : palette.tertiaryFixed,
                                color: row.isPaid ? palette.primary : palette.onTertiaryFixedVariant,
                                borderRadius: 6,
                                padding: '2px 8px',
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.isPaid ? 'Settled' : 'Pending'}
                            </span>
                          </div>

                          <select
                            value={row.vat != null ? String(row.vat) : ''}
                            onChange={(e) => setRow((r) => ({ ...r, vat: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                            disabled={vatRatesLoading}
                            style={inputStyle}
                          >
                            <option value="">{vatRatesLoading ? 'Loading…' : 'Select VAT'}</option>
                            {vatRates.map((rate) => (
                              <option key={rate.id} value={rate.id}>
                                {rate.percentage}%{rate.name ? ` · ${rate.name}` : ''}
                              </option>
                            ))}
                          </select>

                          <div
                            style={{ display: 'flex', justifyContent: 'flex-end', color: palette.onSurfaceVariant, padding: 6 }}
                            title={key === 'balance' ? 'Always the last payment — calculated from the total' : 'Created from the unit extras'}
                          >
                            <i className="ri-lock-line" />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            padding: '20px 32px',
            borderTop: `1px solid ${palette.outlineVariant}33`,
            background: palette.surfaceContainerHighest,
          }}
        >
          <button
            type="button"
            onClick={toggle}
            disabled={saving}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              fontWeight: 700,
              color: palette.onSurfaceVariant,
              background: 'transparent',
              border: `1px solid ${palette.outlineVariant}88`,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            title={!saving && !hasChanges ? 'No changes to save' : undefined}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              fontWeight: 700,
              color: palette.onPrimary,
              background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryContainer})`,
              border: 'none',
              cursor: saving || !hasChanges ? 'not-allowed' : 'pointer',
              opacity: saving || !hasChanges ? 0.5 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>
      </div>

      <AddUnitPicker
        isOpen={addUnitOpen}
        toggle={() => setAddUnitOpen(false)}
        excludeUnitIds={unitPrices.map((unit) => unit.unitId)}
        onAdd={handleAddUnit}
      />
    </Modal>
  );
};

export default EditPaymentModal;
